/**
 * LeavesTab - Gestionare Concedii și Absențe
 */
import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Calendar, CheckCircle, XCircle, Download, Clock, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LeavesTab({ leaves, holidays, yearlyLeaveData, onRefresh }) {
  const [showHistory, setShowHistory] = useState(false);
  const [showHolidays, setShowHolidays] = useState(false);
  const [processingId, setProcessingId] = useState(null);

  const pendingLeaves = leaves.filter(l => l.status === 'pending');
  const processedLeaves = leaves.filter(l => l.status !== 'pending');

  const handleApproveLeave = async (leaveId, approved) => {
    setProcessingId(leaveId);
    try {
      await axios.post(`${API}/leaves/approve`, {
        leave_id: leaveId,
        approved,
        approved_by: "Admin"
      });
      toast.success(approved ? "Concediu aprobat!" : "Concediu respins!");
      onRefresh();
    } catch (error) {
      toast.error("Eroare la procesare");
    } finally {
      setProcessingId(null);
    }
  };

  const downloadLeaveTemplate = async (leaveId) => {
    try {
      const response = await axios.get(`${API}/leaves/${leaveId}/template`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cerere_concediu_${leaveId}.html`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Eroare la descărcare");
    }
  };

  const leaveTypeLabels = {
    CO: "Concediu Odihnă",
    CM: "Concediu Medical",
    CS: "Concediu Special",
    FS: "Fără Salariu",
    IC: "Invoiri/Recuperări"
  };

  const leaveTypeColors = {
    CO: "bg-green-100 text-green-700",
    CM: "bg-red-100 text-red-700",
    CS: "bg-purple-100 text-purple-700",
    FS: "bg-slate-100 text-slate-700",
    IC: "bg-blue-100 text-blue-700"
  };

  return (
    <div className="space-y-4">
      {/* Cereri în Așteptare - PRINCIPAL */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="pb-3">
          <CardTitle style={{fontFamily: 'Space Grotesk, sans-serif'}} className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-yellow-600" />
            Cereri în Așteptare
            {pendingLeaves.length > 0 && (
              <Badge className="bg-yellow-500 ml-2">{pendingLeaves.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {pendingLeaves.length === 0 ? (
            <p className="text-slate-500 text-center py-3 text-sm">Nu există cereri în așteptare</p>
          ) : (
            <div className="space-y-3">
              {pendingLeaves.map((leave) => {
                const userYearly = yearlyLeaveData[leave.user_id] || {};
                const remainingDays = 21 - (userYearly.total_leave_days || 0);
                const wouldExceed = remainingDays < leave.days_count;
                
                return (
                  <div 
                    key={leave.id} 
                    className={`p-3 rounded-lg border ${wouldExceed ? 'bg-red-50 border-red-300' : 'bg-yellow-50 border-yellow-200'}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold">{leave.user_name}</p>
                          <Badge className={`${leaveTypeColors[leave.type]} text-xs`}>
                            {leaveTypeLabels[leave.type] || leave.type}
                          </Badge>
                          <Badge variant="outline" className="text-xs">{leave.days_count} zile</Badge>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">
                          {new Date(leave.start_date).toLocaleDateString('ro-RO')} - {new Date(leave.end_date).toLocaleDateString('ro-RO')}
                        </p>
                        {leave.reason && (
                          <p className="text-xs text-slate-500 mt-1 italic truncate">"{leave.reason}"</p>
                        )}
                        {wouldExceed && (
                          <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Depășește limita! ({userYearly.total_leave_days || 0}/21 zile folosite)
                          </p>
                        )}
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button 
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 h-8 px-2"
                          onClick={() => handleApproveLeave(leave.id, true)}
                          disabled={processingId === leave.id}
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm"
                          variant="destructive"
                          className="h-8 px-2"
                          onClick={() => handleApproveLeave(leave.id, false)}
                          disabled={processingId === leave.id}
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Istoric Concedii - COMPRIMAT */}
      <div 
        className="bg-white rounded-lg border shadow-sm cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setShowHistory(!showHistory)}
      >
        <div className="flex items-center justify-between p-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-600" />
            <span className="font-medium text-sm">Istoric Concedii</span>
            <Badge variant="outline" className="text-xs">{processedLeaves.length}</Badge>
          </div>
          {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
        {showHistory && (
          <div className="px-3 pb-3 border-t">
            <ScrollArea className="max-h-[250px] mt-2">
              {processedLeaves.length === 0 ? (
                <p className="text-slate-500 text-center py-2 text-xs">Nu există concedii procesate</p>
              ) : (
                <div className="space-y-2">
                  {processedLeaves.map((leave) => {
                    const startDate = new Date(leave.start_date);
                    const endDate = new Date(leave.end_date);
                    const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
                    
                    return (
                      <div 
                        key={leave.id} 
                        className={`p-3 rounded-lg text-sm border ${
                          leave.status === 'approved' 
                            ? 'bg-green-50 border-green-200' 
                            : 'bg-red-50 border-red-200'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-slate-800">{leave.user_name}</span>
                              <Badge className={`${leaveTypeColors[leave.type]} text-xs`}>
                                {leave.type === 'CO' ? 'Concediu Odihnă' :
                                 leave.type === 'CM' ? 'Concediu Medical' :
                                 leave.type === 'CS' ? 'Concediu Special' :
                                 leave.type === 'FS' ? 'Fără Salariu' : leave.type}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-slate-600">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {startDate.toLocaleDateString('ro-RO', {day:'2-digit', month:'long', year:'numeric'})}
                                {' → '}
                                {endDate.toLocaleDateString('ro-RO', {day:'2-digit', month:'long', year:'numeric'})}
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {days} {days === 1 ? 'zi' : 'zile'}
                              </Badge>
                            </div>
                            {leave.reason && (
                              <p className="text-xs text-slate-500 italic">"{leave.reason}"</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={`text-xs ${leave.status === 'approved' ? 'bg-green-500' : 'bg-red-500'}`}>
                              {leave.status === 'approved' ? '✓ Aprobat' : '✗ Respins'}
                            </Badge>
                            {leave.status === 'approved' && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="h-7 px-2"
                                onClick={(e) => { e.stopPropagation(); downloadLeaveTemplate(leave.id); }}
                              >
                                <Download className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </div>
        )}
      </div>

      {/* Sărbători Legale - COMPRIMAT */}
      <div 
        className="bg-white rounded-lg border shadow-sm cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setShowHolidays(!showHolidays)}
      >
        <div className="flex items-center justify-between p-3">
          <div className="flex items-center gap-2">
            <span className="text-red-500">🎄</span>
            <span className="font-medium text-sm">Sărbători Legale {new Date().getFullYear()}</span>
            <Badge variant="outline" className="text-xs">{holidays.length}</Badge>
          </div>
          {showHolidays ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
        {showHolidays && (
          <div className="px-3 pb-3 border-t">
            <div className="flex flex-wrap gap-2 mt-2">
              {holidays.map((h) => (
                <div key={h.id} className="px-2 py-1 bg-red-50 rounded border border-red-200 text-xs">
                  <span className="font-semibold text-red-700">
                    {new Date(h.date).toLocaleDateString('ro-RO', { day: 'numeric', month: 'short' })}
                  </span>
                  <span className="text-red-600 ml-1">{h.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
